import { ChangeDetectionStrategy, Component, Input, inject } from '@angular/core';
import { Router } from '@angular/router';
import { TrackingService } from '@celerofinancas/service-utils';
import { NotificationCenterComponent } from '@pages/notification-center/notification-center.component';
import { CAIXA_TRACKING_MODELS } from '@shared/models/tracking-models.enum';
import { LINKS } from '../../../configs/environments-ref';

/**
 * Window augmented with the Intercom messenger global installed by the
 * index.html snippet. Optional because the widget script loads asynchronously.
 */
type IntercomWindow = Window & { Intercom?: (command: 'show') => void };

/**
 * Navigation bar rendered above every iframe screen (86e2dzu8y): "Meu
 * Financeiro" pinned to the left, "Conectar Contas", "Ajuda" and the
 * notification bell pinned to the right. Sits above the accounts header on the
 * screens that have one.
 */
@Component({
  selector: 'caixa-webcomponent-navigation-bar',
  standalone: true,
  templateUrl: './navigation-bar.component.html',
  styleUrl: './navigation-bar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NotificationCenterComponent,
  ],
})
export class NavigationBarComponent {
  /**
   * Whether to render the "Meu Financeiro" navigation button. Disabled on the
   * dashboard, which is the "Meu Financeiro" screen itself.
   */
  @Input()
  public showMyFinance = true;

  /**
   * Whether to render the "Conectar Contas" button (86e2py9g9). Disabled on
   * the dashboard, which keeps only the promotional card.
   */
  @Input()
  public showConnectAccounts = true;

  /**
   * Tracking service (Segment).
   */
  private readonly tracking = inject(TrackingService);

  /**
   * Router used by the "Meu Financeiro" navigation button.
   */
  private readonly router = inject(Router);

  /**
   * Navigates to the "Meu Financeiro" dashboard and emits the Segment event.
   */
  public goToMyFinance(): void {
    this.tracking.track(CAIXA_TRACKING_MODELS.navigateMyFinance);
    this.router.navigate(['/dashboard-finance']);
  }

  /**
   * Opens the Open Finance consent journey in a new tab (same behaviour as the
   * dashboard promotional card) and emits the Segment event.
   */
  public connectAccounts(): void {
    this.tracking.track(CAIXA_TRACKING_MODELS.connectAccountsOpenFinance);
    window.open(LINKS.open_finance, '_blank');
  }

  /**
   * Opens the Intercom messenger and scrolls the iframe to the bottom so the
   * widget (anchored at the bottom-right corner) becomes visible. Emits the
   * Segment event even when the widget script has not finished loading.
   */
  public openHelp(): void {
    this.tracking.track(CAIXA_TRACKING_MODELS.openHelp);
    (window as IntercomWindow).Intercom?.('show');
    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
  }
}
